import type { ReactNode } from 'react'
import { useNls } from '../../nls/nlsProvider'
import { useDisplayName } from '../../hooks/useDisplayName'
import type { ChecklistItem } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import type { ItemAttachmentTarget } from '../../services/itemAttachmentService'
import { ItemAttachmentsPanel } from '../Forms/ItemAttachmentsPanel'
import { Button, DatePicker, Select, StatusChip, Textarea, TextInput } from '../Common'
import { MemberBottomSheet } from './controls/MemberControlsPrimitives'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'

const normalizeDateForInput = (raw?: string | null): string => {
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  if (raw.includes('T')) {
    const head = raw.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head
  }
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}

const checklistStatuses = [
  'CREATED', 'ASSIGNED', 'ASSESSING', 'ACCEPTED', 'ONGOING', 'COMPLETED', 'NOT_REQUIRED', 'POSTPONED',
] as const

const checklistStatusKey: Record<(typeof checklistStatuses)[number], Parameters<ReturnType<typeof useNls>['t']>[0]> = {
  CREATED: 'NLSMemberStatusCreated',
  ASSIGNED: 'NLSMemberStatusAssigned',
  ASSESSING: 'NLSMemberStatusAssessing',
  ACCEPTED: 'NLSMemberStatusAccepted',
  ONGOING: 'NLSMemberStatusOngoing',
  COMPLETED: 'NLSMemberStatusCompleted',
  NOT_REQUIRED: 'NLSMemberStatusNotRequired',
  POSTPONED: 'NLSMemberStatusPostponed',
}

const elementStatusKey = {
  TODO: 'NLSMemberNodeDetailsStatusTodo',
  IN_PROGRESS: 'NLSMemberNodeDetailsStatusInProgress',
  COMPLETE: 'NLSMemberNodeDetailsStatusComplete',
} as const

const formatDate = (value: string) => {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`)
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
}

export const MemberNodeDetailsSheet = ({
  open,
  onClose,
  element,
  checklist,
  canEdit,
  isSaving,
  hasPendingChanges,
  onFieldChange,
  onSave,
  onDiscard,
  attachmentTarget,
  actions,
}: {
  open: boolean
  onClose: () => void
  element: TimelineElement | null
  checklist: ChecklistItem | null
  canEdit: boolean
  isSaving: boolean
  hasPendingChanges: boolean
  onFieldChange: (key: string, value: unknown) => void
  onSave: () => void
  onDiscard: () => void
  attachmentTarget: ItemAttachmentTarget | null
  actions?: ReactNode
  /** Notification context deliberately omits disabled edit controls. */
  readOnlySummaryOnly?: boolean
}) => {
  const { t } = useNls()
  const assigneeName = useDisplayName(checklist?.assigneeName ? { displayName: checklist.assigneeName } : null)
  const title = element?.title ?? checklist?.title ?? t('NLSMemberNodeDetailsTitle')
  const { todayIso } = useWorkspaceDay(element?.workspaceId ?? checklist?.workspaceId)
  const elementStatus = element?.boardStatus ?? element?.workflowStatus ?? 'TODO'
  const dueDate = element?.endDate ?? null
  const isOverdue = Boolean(dueDate && dueDate.slice(0, 10) < todayIso && elementStatus !== 'COMPLETE')
  const footer = canEdit ? (
    <footer className="memberBoardDetailsSheet__footer">
      <Button variant="secondary" size="lg" onClick={onDiscard} data-testid="member-board-details-discard">
        {t('NLSMemberNodeDetailsDiscard')}
      </Button>
      <Button
        variant="primary"
        size="lg"
        disabled={!hasPendingChanges || isSaving}
        onClick={onSave}
        data-testid="member-board-details-save"
      >
        {isSaving ? t('NLSMemberNodeDetailsSaving') : t('NLSMemberNodeDetailsSave')}
      </Button>
    </footer>
  ) : null

  return (
    <MemberBottomSheet open={open} onClose={onClose} title={title} testId="member-board-details-sheet" footer={footer}>
      <div className="memberBoardDetailsSheet">
        <div className="memberBoardDetailsSheet__eyebrow">
          <StatusChip size="sm" tone="info" className="memberBoardDetailsSheet__badge">
            {element?.type ?? (checklist ? t('NLSMemberNodeDetailsChecklist') : t('NLSMemberNodeDetailsFolder'))}
          </StatusChip>
        </div>

        {element ? (
          <section className="memberBoardDetailsSheet__summary" aria-label={t('NLSMemberNodeDetailsSummary')}>
            <div className="memberBoardDetailsSheet__summaryRow">
              <span>{t('NLSMemberNodeDetailsStatus')}</span>
              <strong>{t(elementStatusKey[elementStatus])}</strong>
            </div>
            {dueDate ? <div className={`memberBoardDetailsSheet__summaryRow${isOverdue ? ' isOverdue' : ''}`}><span>{t('NLSMemberNodeDetailsDue')}</span><strong>{formatDate(dueDate)}</strong></div> : null}
            <div className="memberBoardDetailsSheet__summaryRow memberBoardDetailsSheet__summaryProgress">
              <span>{t('NLSMemberNodeDetailsProgress')}</span>
              <strong>{Math.round(element.progress)}%</strong>
              <i aria-hidden="true"><b style={{ width: `${Math.min(100, Math.max(0, element.progress))}%` }} /></i>
            </div>
            {element.totalChecklistCount ? <div className="memberBoardDetailsSheet__summaryRow"><span>{t('NLSMemberNodeDetailsChecklistReadiness')}</span><strong>{element.readyChecklistCount ?? 0} / {element.totalChecklistCount}</strong></div> : null}
            {element.priority ? <div className="memberBoardDetailsSheet__summaryRow"><span>{t('NLSMemberNodeDetailsPriority')}</span><strong>{element.priority}</strong></div> : null}
          </section>
        ) : null}

        {checklist?.assigneeId ? <section className="memberBoardDetailsSheet__summary" aria-label={t('NLSMemberNodeDetailsSummary')}><div className="memberBoardDetailsSheet__summaryRow"><span>{t('NLSMemberNodeDetailsAssignee')}</span><strong>{checklist.assigneeName ? assigneeName : t('NLSMemberNodeDetailsUnassigned')}</strong></div></section> : null}

        {element && canEdit ? (
          <>
            <div className="memberBoardDetailsSheet__card">
              <div className="memberBoardDetailsSheet__field">
                <label htmlFor="member-details-name">{t('NLSMemberNodeDetailsName')}</label>
                <TextInput id="member-details-name" value={element.title} disabled={!canEdit} onChange={(val) => onFieldChange('title', val)} className="memberBoardDetailsSheet__input" />
              </div>
              <div className="memberBoardDetailsSheet__field">
                <label htmlFor="member-details-description">{t('NLSMemberNodeDetailsDescription')}</label>
                <Textarea id="member-details-description" value={element.description ?? ''} disabled={!canEdit} onChange={(val) => onFieldChange('description', val)} className="memberBoardDetailsSheet__textarea" rows={3} />
              </div>
            </div>
            <div className="memberBoardDetailsSheet__card">
              <div className="memberBoardDetailsSheet__row">
                <div className="memberBoardDetailsSheet__field">
                  <label>{t('NLSMemberNodeDetailsStart')}</label>
                  <DatePicker value={normalizeDateForInput(element.startDate)} disabled={!canEdit} onChange={(raw) => onFieldChange('startDate', raw)} ariaLabel={t('NLSMemberNodeDetailsStartDate')} className="memberBoardDetailsSheet__datePicker" />
                </div>
                <div className="memberBoardDetailsSheet__field">
                  <label>{t('NLSMemberNodeDetailsEnd')}</label>
                  <DatePicker value={normalizeDateForInput(element.endDate)} disabled={!canEdit} onChange={(raw) => onFieldChange('endDate', raw)} ariaLabel={t('NLSMemberNodeDetailsEndDate')} className="memberBoardDetailsSheet__datePicker" />
                </div>
              </div>
            </div>
          </>
        ) : null}

        {checklist && canEdit ? (
          <>
            <div className="memberBoardDetailsSheet__card">
              <div className="memberBoardDetailsSheet__field">
                <label htmlFor="member-details-checklist-name">{t('NLSMemberNodeDetailsName')}</label>
                <TextInput id="member-details-checklist-name" value={checklist.title} disabled={!canEdit} onChange={(val) => onFieldChange('title', val)} className="memberBoardDetailsSheet__input" />
              </div>
              <div className="memberBoardDetailsSheet__field">
                <label>{t('NLSMemberNodeDetailsStatus')}</label>
                <Select trigger="pill" ariaLabel={t('NLSMemberNodeDetailsWorkflowStatus')} value={checklist.workflowStatus ?? 'CREATED'} disabled={!canEdit} options={checklistStatuses.map((status) => ({ value: status, label: t(checklistStatusKey[status]) }))} onChange={(next) => onFieldChange('workflowStatus', next)} />
              </div>
            </div>
            <div className="memberBoardDetailsSheet__card">
              <div className="memberBoardDetailsSheet__row">
                <div className="memberBoardDetailsSheet__field"><label>{t('NLSMemberNodeDetailsWeight')}</label><TextInput type="number" min={1} value={String(checklist.weightage)} disabled={!canEdit} onChange={(val) => onFieldChange('weightage', Math.max(1, Math.round(Number(val) || 1)))} className="memberBoardDetailsSheet__input" /></div>
                <div className="memberBoardDetailsSheet__field"><label>{t('NLSMemberNodeDetailsComplexity')}</label><TextInput type="number" min={1} max={5} value={String(checklist.complexityStars ?? 3)} disabled={!canEdit} onChange={(val) => onFieldChange('complexityStars', Math.min(5, Math.max(1, Math.round(Number(val) || 3))))} className="memberBoardDetailsSheet__input" /></div>
              </div>
              <div className="memberBoardDetailsSheet__row">
                <div className="memberBoardDetailsSheet__field"><label>{t('NLSMemberNodeDetailsHours')}</label><TextInput type="number" min={0} value={String(checklist.estimatedHours ?? 0)} disabled={!canEdit} onChange={(val) => onFieldChange('estimatedHours', Math.max(0, Math.round(Number(val) || 0)))} className="memberBoardDetailsSheet__input" /></div>
                <div className="memberBoardDetailsSheet__field"><label>{t('NLSMemberNodeDetailsCost')}</label><TextInput type="number" min={0} value={String(checklist.actualCost ?? 0)} disabled={!canEdit} onChange={(val) => onFieldChange('actualCost', Math.max(0, Math.round(Number(val) || 0)))} className="memberBoardDetailsSheet__input" /></div>
              </div>
            </div>
          </>
        ) : null}

        {attachmentTarget ? <div className="memberBoardDetailsSheet__card memberBoardDetailsSheet__card--attachments"><ItemAttachmentsPanel target={attachmentTarget} budgetLimit={element?.budgetLimit ?? checklist?.actualCost} estimatedCost={element?.estimatedCost} canEdit={canEdit} /></div> : null}
        {actions ? <div className="memberBoardDetailsSheet__actions">{actions}</div> : null}
      </div>
    </MemberBottomSheet>
  )
}
